import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { registerChannel, unregisterChannel } from "@/lib/realtimeChannels";

/**
 * Tracks FIG Verify scans that are still running on the server. The Analyze
 * page inserts a placeholder row in `scans` (processing_status='processing')
 * before calling fig-verify, then registers the row id here. We subscribe to
 * realtime UPDATEs on that row so a toast fires the moment the edge function
 * finishes — even if the user has already left /analyze for /home.
 */

type Status = "processing" | "complete" | "failed";

interface PendingScan {
  scanId: string;
  startedAt: number;
}

interface CompletedSummary {
  scanId: string;
  itemType: string;
  status: "complete" | "failed";
  errorMessage?: string | null;
}

interface Ctx {
  pendingScans: PendingScan[];
  unviewedCount: number;
  lastReady: CompletedSummary | null;
  registerScan: (scanId: string) => void;
  clearLastReady: () => void;
  refreshUnviewed: () => Promise<void>;
  openLatestUnviewed: () => Promise<void>;
}

const BackgroundScansContext = createContext<Ctx | null>(null);

export function BackgroundScansProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [pendingScans, setPendingScans] = useState<PendingScan[]>([]);
  const [unviewedCount, setUnviewedCount] = useState(0);
  const [lastReady, setLastReady] = useState<CompletedSummary | null>(null);
  const subscribedScanIds = useRef<Set<string>>(new Set());

  const refreshUnviewed = useCallback(async () => {
    if (!user) { setUnviewedCount(0); return; }
    const { count } = await supabase
      .from("scans")
      .select("id", { count: "exact", head: true })
      .eq("user_id", user.id)
      .eq("processing_status", "complete")
      .is("viewed_at", null);
    setUnviewedCount(count ?? 0);
  }, [user]);

  // Pull any rows still processing (e.g. after a hard refresh) and any
  // unviewed completes so the badge / banner survive reloads.
  useEffect(() => {
    if (!user) {
      setPendingScans([]);
      setUnviewedCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      // Only track bulk-queue scans (bulk_job_id IS NOT NULL). Single
      // verifications are fully synchronous and never need background tracking.
      const { data } = await supabase
        .from("scans")
        .select("id, created_at")
        .eq("user_id", user.id)
        .eq("processing_status", "processing")
        .not("bulk_job_id", "is", null)
        .order("created_at", { ascending: true });
      if (cancelled) return;
      const rows = (data ?? []) as { id: string; created_at: string }[];
      setPendingScans(rows.map((r) => ({ scanId: r.id, startedAt: new Date(r.created_at).getTime() })));
      rows.forEach((r) => subscribe(r.id));
      void refreshUnviewed();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleRowUpdate = useCallback((scanId: string, row: Record<string, unknown>) => {
    const status = String(row.processing_status ?? "") as Status;
    if (status !== "complete" && status !== "failed") return;
    const result = row.result as { item?: { detected_type?: string } } | null;
    const itemType = result?.item?.detected_type || "vintage item";
    const errorMessage = (row.error_message as string | null) ?? null;

    setPendingScans((prev) => prev.filter((p) => p.scanId !== scanId));
    void refreshUnviewed();

    const summary: CompletedSummary = {
      scanId,
      itemType,
      status,
      errorMessage,
    };
    setLastReady(summary);

    if (status === "complete") {
      toast.success("Verification complete — tap to review", {
        description: `Your ${itemType} is ready for review`,
        duration: Infinity,
        onDismiss: () => { /* user dismissed; badge in profile still indicates */ },
        onAutoClose: () => {},
        action: {
          label: "Review",
          onClick: () => nav(`/result?scanId=${scanId}`),
        },
      });
    } else {
      toast.error("Verification couldn't complete", {
        description: errorMessage || "Something went wrong — tap to retry",
        duration: Infinity,
        action: {
          label: "Retry",
          onClick: () => nav("/scan"),
        },
      });
    }

    if (subscribedScanIds.current.has(scanId)) {
      unregisterChannel(`bg-scan-${scanId}`);
      subscribedScanIds.current.delete(scanId);
    }
  }, [nav, refreshUnviewed]);

  const subscribe = useCallback((scanId: string) => {
    if (subscribedScanIds.current.has(scanId)) return;
    const key = `bg-scan-${scanId}`;
    registerChannel(key, () =>
      supabase
        .channel(key)
        .on(
          "postgres_changes",
          { event: "UPDATE", schema: "public", table: "scans", filter: `id=eq.${scanId}` },
          (payload) => handleRowUpdate(scanId, payload.new as Record<string, unknown>),
        )
        .subscribe()
    );
    subscribedScanIds.current.add(scanId);
  }, [handleRowUpdate]);

  // Poll fallback: realtime UPDATE events can be missed (network blip,
  // socket reconnect timing, RLS edge cases). Every 5s we check any
  // pending scan rows directly so completion is never lost.
  useEffect(() => {
    if (pendingScans.length === 0) return;
    const t = setInterval(async () => {
      for (const p of pendingScans) {
        const { data } = await supabase
          .from("scans")
          .select("id, processing_status, result, error_message")
          .eq("id", p.scanId)
          .maybeSingle();
        if (data && (data.processing_status === "complete" || data.processing_status === "failed")) {
          handleRowUpdate(p.scanId, data as Record<string, unknown>);
        }
      }
    }, 5000);
    return () => clearInterval(t);
  }, [pendingScans, handleRowUpdate]);

  const registerScan = useCallback((scanId: string) => {
    setPendingScans((prev) => prev.some((p) => p.scanId === scanId)
      ? prev
      : [...prev, { scanId, startedAt: Date.now() }]);
    subscribe(scanId);
  }, [subscribe]);

  const clearLastReady = useCallback(() => setLastReady(null), []);

  // Resolve the most recent unviewed completed scan and navigate to it.
  // Used by surfaces (profile badge, post-choice banner) that may render
  // after a refresh — when `lastReady` is null but unviewed rows exist
  // in the database.
  const openLatestUnviewed = useCallback(async () => {
    if (lastReady?.scanId) { nav(`/result?scanId=${lastReady.scanId}`); return; }
    if (!user) return;
    const { data } = await supabase
      .from("scans")
      .select("id")
      .eq("user_id", user.id)
      .eq("processing_status", "complete")
      .is("viewed_at", null)
      .order("completed_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (data?.id) nav(`/result?scanId=${data.id}`);
  }, [lastReady, nav, user]);

  // Tear down all channels on unmount.
  useEffect(() => {
    const ids = subscribedScanIds.current;
    return () => {
      ids.forEach((scanId) => unregisterChannel(`bg-scan-${scanId}`));
      ids.clear();
    };
  }, []);

  const value = useMemo<Ctx>(() => ({
    pendingScans,
    unviewedCount,
    lastReady,
    registerScan,
    clearLastReady,
    refreshUnviewed,
    openLatestUnviewed,
  }), [pendingScans, unviewedCount, lastReady, registerScan, clearLastReady, refreshUnviewed, openLatestUnviewed]);

  return (
    <BackgroundScansContext.Provider value={value}>
      {children}
    </BackgroundScansContext.Provider>
  );
}

export function useBackgroundScans() {
  const ctx = useContext(BackgroundScansContext);
  if (!ctx) throw new Error("useBackgroundScans must be used inside BackgroundScansProvider");
  return ctx;
}